import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import type { LogEntry, LogSource } from "../types";

const ALL_SOURCES: LogSource[] = ["cron", "backend", "frontend"];

export function DebugLog() {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [sources, setSources] = useState<Set<LogSource>>(new Set(ALL_SOURCES));
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  async function load() {
    try {
      const active = ALL_SOURCES.filter((s) => sources.has(s));
      const rows = await api.getLogs(active.length === ALL_SOURCES.length ? [] : active);
      setEntries(rows);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  useEffect(() => {
    void load();
    if (autoRefresh) {
      timer.current = window.setInterval(() => void load(), 2000);
      return () => {
        if (timer.current) window.clearInterval(timer.current);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, sources]);

  function toggleSource(s: LogSource) {
    setSources((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next.size === 0 ? new Set(ALL_SOURCES) : next;
    });
  }

  return (
    <div className="debug-log">
      <div className="debug-controls">
        <div className="source-filters">
          {ALL_SOURCES.map((s) => (
            <label key={s} className={`chip ${s} ${sources.has(s) ? "on" : ""}`}>
              <input type="checkbox" checked={sources.has(s)} onChange={() => toggleSource(s)} />
              {s}
            </label>
          ))}
        </div>
        <label className="auto-refresh">
          <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
          auto-refresh
        </label>
        <button className="icon-btn" onClick={() => void load()} title="Refresh now">
          ⟳
        </button>
      </div>
      {error && <div className="notice error">{error}</div>}
      <div className="log-stream scroll">
        {entries.length === 0 && <div className="notice muted">No log entries.</div>}
        {entries.map((e, i) => (
          <div key={i} className={`log-row ${e.level}`}>
            <span className="log-ts">{formatTs(e.ts)}</span>
            <span className={`log-source ${e.source}`}>{e.source}</span>
            <span className="log-msg">
              {e.message}
              {e.detail != null && (
                <span className="log-detail">{typeof e.detail === "string" ? e.detail : JSON.stringify(e.detail)}</span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatTs(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour12: false }) + "." + String(d.getMilliseconds()).padStart(3, "0");
}
